import { forwardRef, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Easing,
  Platform,
  Pressable,
  type PressableProps,
  StyleSheet,
  type View,
  type ViewStyle,
} from 'react-native';
import { useFocusVisible } from '../../hooks/useFocusVisible';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { tokens } from '../../theme/tokens';
import { Text } from './Text';

interface ButtonProps extends Omit<PressableProps, 'children'> {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
  textColor?: string;
  pill?: boolean;
}

export const Button = forwardRef<View, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      disabled,
      style,
      onBlur,
      onFocus,
      onPressIn,
      onPressOut,
      textColor,
      pill = false,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const scaleValue = useRef(new Animated.Value(1)).current;
    const reducedMotion = useReducedMotion();
    const [focused, setFocused] = useState(false);
    // The ring follows the input modality, as CSS :focus-visible does — a
    // pointer press should not outline what it just pressed, and a keyboard
    // user must still see where they are.
    const focusRingVisible = useFocusVisible();
    const [hovered, setHovered] = useState(false);
    const minimumTarget =
      Platform.OS === 'android'
        ? tokens.interaction.minimumTarget.android
        : tokens.interaction.minimumTarget.default;

    const defaultTextColor = (() => {
      if (variant === 'primary') return colors.background.primary;
      if (variant === 'outline' || variant === 'ghost') return colors.primary[500];
      return colors.text.primary;
    })();

    useEffect(() => {
      if (reducedMotion) scaleValue.setValue(1);
    }, [reducedMotion, scaleValue]);

    const handlePressIn: NonNullable<PressableProps['onPressIn']> = useCallback(
      (event) => {
        if (reducedMotion) {
          scaleValue.setValue(1);
        } else {
          Animated.timing(scaleValue, {
            toValue: 0.98,
            duration: tokens.duration.instant,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }).start();
        }
        onPressIn?.(event);
      },
      [onPressIn, reducedMotion, scaleValue],
    );

    const handlePressOut: NonNullable<PressableProps['onPressOut']> = useCallback(
      (event) => {
        if (reducedMotion) {
          scaleValue.setValue(1);
        } else {
          Animated.timing(scaleValue, {
            toValue: 1,
            duration: tokens.duration.instant,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }).start();
        }
        onPressOut?.(event);
      },
      [onPressOut, reducedMotion, scaleValue],
    );

    const handleFocus: NonNullable<PressableProps['onFocus']> = useCallback(
      (event) => {
        if (Platform.OS === 'web') setFocused(true);
        onFocus?.(event);
      },
      [onFocus],
    );

    const handleBlur: NonNullable<PressableProps['onBlur']> = useCallback(
      (event) => {
        setFocused(false);
        onBlur?.(event);
      },
      [onBlur],
    );

    // Buttons had a press animation but no hover response at all, so on the
    // web they felt inert next to cards, which lift and warm their border via
    // cardSurface(). onHoverIn/Out are web-only in RN and simply never fire on
    // touch, so no platform branch is needed.
    const handleHoverIn = useCallback(() => setHovered(true), []);
    const handleHoverOut = useCallback(() => setHovered(false), []);

    return (
      <Animated.View style={{ transform: [{ scale: scaleValue }] }}>
        <Pressable
          ref={ref}
          accessibilityRole="button"
          disabled={isDisabled}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onPressIn={handlePressIn}
          onPressOut={handlePressOut}
          onHoverIn={handleHoverIn}
          onHoverOut={handleHoverOut}
          style={(state) => [
            styles.base,
            styles[variant],
            styles[size],
            fullWidth && styles.fullWidth,
            pill && styles.pill,
            typeof style === 'function' ? style(state) : style,
            { minHeight: minimumTarget, minWidth: minimumTarget },
            !reducedMotion && transitionStyle,
            hovered && !isDisabled && hoverStyles[variant],
            state.pressed && !isDisabled && styles.pressed,
            focused && focusRingVisible && styles.focusVisible,
            isDisabled && styles.disabled,
          ]}
          {...props}
        >
          {loading && <ActivityIndicator size="small" color={textColor || defaultTextColor} />}
          {!loading && typeof children === 'string' && (
            <Text
              variant={size === 'xs' ? 'caption' : size === 'sm' ? 'bodySmall' : 'body'}
              color={textColor || defaultTextColor}
              style={{ fontWeight: '500' }}
            >
              {children}
            </Text>
          )}
          {!loading && typeof children !== 'string' && children}
        </Pressable>
      </Animated.View>
    );
  },
);

Button.displayName = 'Button';

// Web-only: RN Native has no CSS transitions, and there is no hover there to
// animate. Mirrors what cardSurface() already does for cards so a button and a
// card respond on the same curve.
const transitionStyle = Platform.select({
  web: {
    transitionProperty: 'background-color, border-color, opacity, box-shadow',
    transitionDuration: `${tokens.duration.fast}ms`,
    transitionTimingFunction: tokens.easing.default,
  },
  default: {},
}) as ViewStyle;

// Each variant warms toward the brand rather than just lightening: a hover
// that only changes opacity reads as a rendering artefact, not an invitation.
const hoverStyles = StyleSheet.create({
  primary: {
    backgroundColor: colors.primary[400],
  },
  secondary: {
    backgroundColor: colors.surface.rowHover,
    borderColor: colors.primary.borderActive,
  },
  outline: {
    backgroundColor: colors.interactive.hoverSubtle,
    borderColor: colors.primary.borderActive,
  },
  ghost: {
    backgroundColor: colors.interactive.hoverSubtle,
  },
});

const styles = StyleSheet.create({
  base: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.md,
    flexDirection: 'row',
  },
  primary: {
    backgroundColor: colors.primary[500],
  },
  secondary: {
    backgroundColor: colors.background.card,
    borderWidth: 1,
    borderColor: colors.border.muted,
  },
  outline: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.border.muted,
  },
  ghost: {
    backgroundColor: 'transparent',
  },
  xs: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xxs,
  },
  sm: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  md: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  lg: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
  },
  fullWidth: {
    width: '100%',
  },
  pill: {
    borderRadius: tokens.radius.full,
  },
  pressed: {
    opacity: 0.9,
  },
  focusVisible:
    Platform.select({
      web: {
        outlineColor: colors.border.focus,
        outlineOffset: tokens.interaction.focusRing.offset,
        outlineStyle: 'solid',
        outlineWidth: tokens.interaction.focusRing.width,
      } as unknown as ViewStyle,
      default: {},
    }) ?? {},
  disabled: {
    opacity: 0.5,
  },
});
